import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  HelpCircle, 
  BookOpen, 
  CreditCard, 
  Shield, 
  Settings, 
  MessageCircle,
  ChevronLeft,
  Search,
  FileText,
  Video
} from 'lucide-react';

const HelpCenter = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategory, setExpandedCategory] = useState(null);

  const categories = [
    {
      id: 'getting-started',
      title: 'البداية والاستخدام',
      icon: BookOpen,
      color: 'blue',
      articles: [
        {
          title: 'كيفية إنشاء حساب جديد',
          description: 'خطوات تفصيلية لإنشاء حسابك الأول في المنصة',
          content: 'لإنشاء حساب جديد، اضغط على زر "تسجيل" في الصفحة الرئيسية، ثم املأ بياناتك الشخصية واتبع التعليمات.'
        },
        {
          title: 'كيفية الاشتراك في الكورسات',
          description: 'تعرف على طريقة الاشتراك والدفع في الكورسات المتاحة',
          content: 'اختر الكورس الذي يناسبك، اضغط على "اشترك الآن"، ثم اتبع خطوات الدفع المتاحة.'
        },
        {
          title: 'استخدام لوحة التحكم',
          description: 'شرح كامل لواجهة لوحة التحكم الشخصية',
          content: 'لوحة التحكم تحتوي على جميع بياناتك، الكورسات المسجلة فيها، والإشعارات.'
        }
      ]
    },
    {
      id: 'courses',
      title: 'الكورسات والدروس',
      icon: Video,
      color: 'emerald',
      articles: [
        {
          title: 'البحث عن الكورسات المناسبة',
          description: 'كيفية العثور على الكورسات التي تناسب مستواك الدراسي',
          content: 'استخدم شريط البحث أو فلتر المواد والصفوف للعثور على الكورسات المناسبة.'
        },
        {
          title: 'مشاهدة الدروس المسجلة',
          description: 'طريقة مشاهدة الدروس والفيديوهات التعليمية',
          content: 'بعد الاشتراك في الكورس، يمكنك مشاهدة جميع الدروس في أي وقت يناسبك.'
        },
        {
          title: 'تحميل المواد التعليمية',
          description: 'كيفية تحميل الملفات والمواد التدريبية',
          content: 'من صفحة الكورس، اضغط على زر التحميل بجانب كل مادة متاحة.'
        }
      ]
    },
    {
      id: 'payment',
      title: 'الدفع والاشتراكات',
      icon: CreditCard,
      color: 'purple',
      articles: [
        {
          title: 'طرق الدفع المتاحة',
          description: 'جميع طرق الدفع المعتمدة في المنصة',
          content: 'نقبل الدفع عبر البطاقات الائتمانية، المحافظ الإلكترونية، والتحويل البنكي.'
        },
        {
          title: 'تجديد الاشتراك',
          description: 'كيفية تجديد اشتراكك في الكورسات',
          content: 'سيتم إشعارك قبل انتهاء الاشتراك، ويمكنك التجديد من لوحة التحكم.'
        },
        {
          title: 'سياسة الاسترداد',
          description: 'شروط استرداد المبالغ المدفوعة',
          content: 'يمكن استرداد المبلغ خلال 7 أيام من تاريخ الدفع بشروط معينة.'
        }
      ]
    },
    {
      id: 'technical',
      title: 'الدعم الفني',
      icon: Settings,
      color: 'orange',
      articles: [
        {
          title: 'مشاكل تسجيل الدخول',
          description: 'حلول لمشاكل تسجيل الدخول الشائعة',
          content: 'تأكد من صحة البريد الإلكتروني وكلمة المرور، أو استخدم خيار "نسيت كلمة المرور".'
        },
        {
          title: 'مشاكل تشغيل الفيديو',
          description: 'حلول لمشاكل تشغيل الدروس المرئية',
          content: 'تحقق من اتصال الإنترنت، جرب متصفحاً آخر، أو قم بتحديث الصفحة.'
        },
        {
          title: 'تغيير كلمة المرور',
          description: 'كيفية تغيير كلمة المرور الخاصة بك',
          content: 'من إعدادات الملف الشخصي، اختر "تغيير كلمة المرور" واتبع الخطوات.'
        }
      ]
    }
  ];

  const quickActions = [
    {
      title: 'تواصل مع الدعم',
      description: 'احصل على مساعدة مباشرة من فريق الدعم',
      icon: MessageCircle,
      link: '/contact',
      color: 'primary'
    },
    {
      title: 'الشروط والأحكام',
      description: 'اطلع على شروط استخدام المنصة',
      icon: FileText,
      link: '/terms',
      color: 'gray'
    },
    {
      title: 'سياسة الخصوصية',
      description: 'تعرف على كيفية حماية بياناتك',
      icon: Shield,
      link: '/privacy',
      color: 'green'
    }
  ];

  const filteredCategories = categories.map(category => ({
    ...category,
    articles: category.articles.filter(article =>
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.articles.length > 0);

  return (
    <div className="min-h-screen bg-gray-50/50">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="w-20 h-20 bg-white/20 rounded-3xl flex items-center justify-center mx-auto mb-6 backdrop-blur-sm">
              <HelpCircle size={40} />
            </div>
            <h1 className="text-4xl font-black mb-4">مركز المساعدة</h1>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              كل ما تحتاج لمعرفته حول استخدام منصة "منصتي" التعليمية
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto relative">
              <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="ابحث عن مساعدة..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-12 pl-4 py-4 rounded-2xl border-0 shadow-xl text-gray-900 placeholder-gray-400 focus:ring-4 focus:ring-primary-300 transition-all"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {quickActions.map((action, index) => (
            <Link
              key={index}
              to={action.link}
              className={`group bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-${action.color}-200`}
            >
              <div className={`w-12 h-12 bg-${action.color}-50 text-${action.color}-600 rounded-xl flex items-center justify-center mb-4 group-hover:bg-${action.color}-600 group-hover:text-white transition-all`}>
                <action.icon size={24} />
              </div>
              <h3 className="text-lg font-black text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                {action.title}
              </h3>
              <p className="text-sm text-gray-600">
                {action.description}
              </p>
            </Link>
          ))}
        </div>

        {/* Categories */}
        {searchTerm && (
          <div className="mb-8">
            <h2 className="text-2xl font-black text-gray-900 mb-4">
              نتائج البحث عن "{searchTerm}"
            </h2>
          </div>
        )}

        <div className="space-y-8">
          {filteredCategories.map((category) => (
            <div key={category.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <button
                onClick={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
                className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 bg-${category.color}-50 text-${category.color}-600 rounded-xl flex items-center justify-center`}>
                    <category.icon size={24} />
                  </div>
                  <div className="text-right">
                    <h3 className="text-xl font-black text-gray-900">{category.title}</h3>
                    <p className="text-sm text-gray-600">{category.articles.length} مقال</p>
                  </div>
                </div>
                <ChevronLeft 
                  className={`text-gray-400 transition-transform duration-300 ${
                    expandedCategory === category.id ? 'rotate-90' : ''
                  }`}
                  size={20} 
                />
              </button>
              
              {expandedCategory === category.id && (
                <div className="border-t border-gray-100 p-6 space-y-4">
                  {category.articles.map((article, index) => (
                    <div key={index} className="border border-gray-100 rounded-xl p-4 hover:bg-gray-50 transition-colors">
                      <h4 className="text-lg font-black text-gray-900 mb-2">{article.title}</h4>
                      <p className="text-sm text-gray-600 mb-3">{article.description}</p>
                      <p className="text-sm text-gray-500 leading-relaxed">{article.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact Support */}
        <div className="mt-16 bg-gradient-to-br from-primary-50 to-primary-100 rounded-3xl p-8 text-center">
          <div className="w-16 h-16 bg-primary-600 text-white rounded-2xl flex items-center justify-center mx-auto mb-6">
            <MessageCircle size={32} />
          </div>
          <h2 className="text-2xl font-black text-gray-900 mb-4">لم تجد ما تبحث عنه؟</h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            فريق الدعم متاح لمساعدتك في أي وقت. لا تتردد في التواصل معنا لأي استفسار أو مشكلة.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-primary-600 text-white px-8 py-3 rounded-2xl font-black hover:bg-primary-700 transition-colors shadow-lg hover:shadow-xl"
          >
            <MessageCircle size={20} />
            تواصل مع الدعم الفني
          </Link>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
